import React from 'react';
import type { UserRole, UserStatus } from '@/api/models';
import {
    canReassignBranchOrRole,
    getAssignableRoles,
    getRoleFields,
    isBranchLocked,
    isValidPassword,
    isValidPin,
    type RoleFields,
} from '../teamMeta';
import { useResetOnKeyChange } from '@/utils';

export type BranchOption = { id: string; name: string };

/**
 * A branch as a *reference* rather than a choice: what it is called and what
 * state it is in. Distinct from {@link BranchOption}, which is the assignable
 * list and so never includes an archived branch — a user already assigned to
 * one still has to render with a name (bos-api BOS-122).
 */
export type BranchRef = {
    id: string;
    /** Null when nothing in this tenant resolves the id. */
    name: string | null;
    archived: boolean;
};

export type UserFormInitialValues = {
    name: string;
    role: UserRole;
    branchId: string | null;
    status: UserStatus;
};

/** Normalised values the modal hands back; the table hook maps these to the
 *  right create/update request per mode. Fields that don't apply are '' / null. */
export type UserFormSubmit = {
    name: string;
    role: UserRole;
    branchId: string | null;
    email: string;
    password: string;
    pin: string;
    status: UserStatus;
};

type UseUserFormParams = {
    mode: 'create' | 'edit';
    isOpen: boolean;
    callerRole: UserRole;
    callerBranchId: string | null;
    /** True when the caller is editing their own row — locks role/branch (and
     *  the status toggle, in the modal) so they can't demote/disable themselves. */
    isSelf?: boolean;
    initialValues?: UserFormInitialValues;
    onSubmit: (input: UserFormSubmit) => void;
};

export type UseUserFormResult = {
    name: string;
    role: UserRole;
    branchId: string;
    email: string;
    password: string;
    pin: string;
    status: UserStatus;
    fields: RoleFields;
    assignableRoles: UserRole[];
    branchLocked: boolean;
    canReassign: boolean;
    isValid: boolean;
    /** i18n key for the password's inline error, or null when there is none. */
    passwordErrorKey: string | null;
    /** i18n key for the PIN's inline error, or null when there is none. */
    pinErrorKey: string | null;
    onNameChange: (value: string) => void;
    onRoleChange: (value: string) => void;
    onBranchChange: (value: string) => void;
    onEmailChange: (value: string) => void;
    onPasswordChange: (value: string) => void;
    onPinChange: (value: string) => void;
    onStatusToggle: (active: boolean) => void;
    onSubmit: () => void;
};

const pickDefaultRole = (assignable: UserRole[]): UserRole =>
    assignable.includes('Seller') ? 'Seller' : (assignable[0] ?? 'Seller');

/**
 * Create/Edit user form state. The visible fields and assignable roles are
 * derived from the *caller's* role (a BranchAdmin can only create Sellers in
 * their own branch) and the *selected* role (the field matrix). Edit mode only
 * touches name + status, plus role/branch when the caller is an OrgAdmin.
 */
export const useUserForm = ({
    mode,
    isOpen,
    callerRole,
    callerBranchId,
    isSelf = false,
    initialValues,
    onSubmit,
}: UseUserFormParams): UseUserFormResult => {
    const assignableRoles = React.useMemo(
        () => getAssignableRoles(callerRole),
        [callerRole],
    );
    const branchLocked = isBranchLocked(callerRole);
    // Even an OrgAdmin can't reassign their own role/branch (self-lockout).
    const canReassign = canReassignBranchOrRole(callerRole) && !isSelf;

    const defaultRole = initialValues?.role ?? pickDefaultRole(assignableRoles);
    const defaultBranchId =
        initialValues?.branchId ?? (branchLocked ? callerBranchId : null) ?? '';

    const [name, setName] = React.useState(initialValues?.name ?? '');
    const [role, setRole] = React.useState<UserRole>(defaultRole);
    const [branchId, setBranchId] = React.useState(defaultBranchId);
    const [email, setEmail] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [pin, setPin] = React.useState('');
    const [status, setStatus] = React.useState<UserStatus>(
        initialValues?.status ?? 'Active',
    );

    // Reset to defaults whenever the modal (re)opens — mirrors useBrandForm.
    // Re-seeds on the open transition alone by design: initialValues and the
    // role/branch defaults are read imperatively at that moment.
    useResetOnKeyChange(isOpen, () => {
        if (!isOpen) {
            return;
        }
        setName(initialValues?.name ?? '');
        setRole(initialValues?.role ?? pickDefaultRole(assignableRoles));
        setBranchId(
            initialValues?.branchId ??
                (branchLocked ? (callerBranchId ?? '') : ''),
        );
        setEmail('');
        setPassword('');
        setPin('');
        setStatus(initialValues?.status ?? 'Active');
    });

    const fields = getRoleFields(role);

    const onNameChange = React.useCallback((v: string) => setName(v), []);
    const onRoleChange = React.useCallback(
        (v: string) => setRole(v as UserRole),
        [],
    );
    const onBranchChange = React.useCallback((v: string) => setBranchId(v), []);
    const onEmailChange = React.useCallback((v: string) => setEmail(v), []);
    const onPasswordChange = React.useCallback(
        (v: string) => setPassword(v),
        [],
    );
    // PIN input only accepts up to 4 digits — strip everything else as typed.
    const onPinChange = React.useCallback(
        (v: string) => setPin(v.replace(/\D/g, '').slice(0, 4)),
        [],
    );
    const onStatusToggle = React.useCallback(
        (active: boolean) => setStatus(active ? 'Active' : 'Disabled'),
        [],
    );

    const trimmedName = name.trim();

    const isValid = React.useMemo(() => {
        if (trimmedName.length === 0) {
            return false;
        }
        if (mode === 'edit') {
            // Edit only changes name + status (+ role/branch for OrgAdmin); a
            // reassignable branch must still resolve to a value.
            if (canReassign && fields.branch && branchId.length === 0) {
                return false;
            }
            return true;
        }
        if (fields.branch && branchId.length === 0) {
            return false;
        }
        if (fields.email && email.trim().length === 0) {
            return false;
        }
        if (fields.password && !isValidPassword(password)) {
            return false;
        }
        // PIN is optional, but if provided it must be exactly 4 digits.
        if (fields.pin && pin.length > 0 && !isValidPin(pin)) {
            return false;
        }
        return true;
    }, [
        branchId,
        canReassign,
        email,
        fields,
        mode,
        password,
        pin,
        trimmedName,
    ]);

    /**
     * What is wrong with the password / PIN as typed, or null.
     *
     * Only once the field has something in it: telling someone their empty
     * password is too short before they have typed a character is noise, and
     * the disabled submit already says "not yet". These say *why* — which a
     * disabled button on its own never did (BOS-138).
     */
    const passwordErrorKey =
        fields.password && password.length > 0 && !isValidPassword(password)
            ? 'team.components.userTable.form.passwordTooShort'
            : null;

    const pinErrorKey =
        fields.pin && pin.length > 0 && !isValidPin(pin)
            ? 'team.components.userTable.form.pinWrongLength'
            : null;

    const onSubmitClick = React.useCallback(() => {
        if (!isValid) {
            return;
        }
        onSubmit({
            name: trimmedName,
            role,
            branchId: branchId.length > 0 ? branchId : null,
            email: email.trim(),
            password,
            pin,
            status,
        });
    }, [
        branchId,
        email,
        isValid,
        onSubmit,
        password,
        pin,
        role,
        status,
        trimmedName,
    ]);

    return {
        name,
        role,
        branchId,
        email,
        password,
        pin,
        status,
        fields,
        assignableRoles,
        branchLocked,
        canReassign,
        isValid,
        passwordErrorKey,
        pinErrorKey,
        onNameChange,
        onRoleChange,
        onBranchChange,
        onEmailChange,
        onPasswordChange,
        onPinChange,
        onStatusToggle,
        onSubmit: onSubmitClick,
    };
};
